import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  MdClose,
  MdEdit,
  MdVideocam,
  MdPeopleOutline,
  MdNotes,
  MdContentCopy,
  MdOpenInNew,
  MdCheckCircle,
  MdDelete,
} from "react-icons/md";
import DatePicker from "rsuite/DatePicker";
import Modal from "rsuite/Modal";
import Button from "rsuite/Button";
import TagInput from "rsuite/TagInput";
import "rsuite/dist/rsuite.min.css";
import toast from "react-hot-toast";
import { getCookie } from "../../../common/Utility";

const CREATE_URL = `${process.env.REACT_APP_CRM}/gmail/meetings/create`;
const UPDATE_URL = `${process.env.REACT_APP_CRM}/gmail/meetings/update`;
const DEFAULT_ATTENDEES = [""];

const MeetingFormModal = ({
  isOpen,
  onClose,
  initialData,
  onSuccess,
  startInViewMode = false,
  currentUser,
  customerEmail,
  customerDetails,
}) => {
  const [formValue, setFormValue] = useState({
    title: "",
    attendees: DEFAULT_ATTENDEES,
    start_time: null,
    end_time: null,
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  let user_uuid = getCookie("muvi_useruuid");

  useEffect(() => {
    if (isOpen) {
      if (initialData && initialData.google_event_id) {
        setIsUpdate(true);
        setViewMode(startInViewMode);
        setFormValue({
          title: initialData.title || "",
          attendees: initialData.attendees,
          start_time: initialData.start ? new Date(initialData.start) : null,
          end_time: initialData.end ? new Date(initialData.end) : null,
          description: initialData.description || "",
        });
      } else if (initialData) {
        setIsUpdate(false);
        setViewMode(false);
        setFormValue({
          title: "",
          attendees: customerEmail ? [customerEmail] : [],
          start_time: initialData.start ? new Date(initialData.start) : null,
          end_time: initialData.end ? new Date(initialData.end) : null,
          description: "",
        });
      } else {
        setIsUpdate(false);
        setViewMode(false);
        setFormValue({
          title: "",
          attendees: customerEmail ? [customerEmail] : [],
          start_time: null,
          end_time: null,
          description: "",
        });
      }
    }
  }, [isOpen, initialData, startInViewMode]);

  const handleChange = (name, value) => {
    setFormValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateTimeChange = (field, newValue, type) => {
    if (!newValue) return;

    const currentDate = formValue[field] || new Date();
    const updatedDate = new Date(currentDate);

    if (type === "date") {
      updatedDate.setFullYear(newValue.getFullYear());
      updatedDate.setMonth(newValue.getMonth());
      updatedDate.setDate(newValue.getDate());
    } else if (type === "time") {
      updatedDate.setHours(newValue.getHours());
      updatedDate.setMinutes(newValue.getMinutes());
      updatedDate.setSeconds(newValue.getSeconds());
    }

    handleChange(field, updatedDate);
  };

  const handleSubmit = async () => {
    if (!formValue.title) {
      toast.error("Title is required");
      return;
    }

    if (!formValue.start_time || !formValue.end_time) {
      toast.error("Start and End times are required");
      return;
    }


    setLoading(true);
    try {
      const payload = {
        email: currentUser.email.toLowerCase(),
        attendees: formValue.attendees,
        title: formValue.title,
        start_time: moment(formValue.start_time).format("YYYY-MM-DDTHH:mm:ssZ"),
        end_time: moment(formValue.end_time).format("YYYY-MM-DDTHH:mm:ssZ"),
        description: formValue.description,
        mautic_id: customerDetails.mautic_id,
        user_uuid: user_uuid
      };

      if (isUpdate) {
        payload.google_event_id = initialData.google_event_id;
        payload.old_last_meeting_booked = customerDetails.last_meeting_booked || 1773828422;
      }

      const res = await fetch(isUpdate ? UPDATE_URL : CREATE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (
        data.status === "success" ||
        data.status === 200 ||
        data.code === "200"
      ) {
        toast.success(
          `Meeting ${isUpdate ? "updated" : "scheduled"} successfully!`,
        );

        onSuccess && onSuccess();
        onClose();
      } else {
        throw new Error(data.message || "Failed to save meeting");
      }
    } catch (err) {
      console.error("MeetingFormModal: Error saving meeting", err);
      toast.error(err.message || "An error occurred while saving the meeting.");

    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMeeting = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_CRM}/gmail/meetings/delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            google_event_id: initialData.google_event_id,
            email: currentUser.email,
            mautic_id: customerDetails.mautic_id,
            old_last_meeting_booked: customerDetails.last_meeting_booked,
            user_uuid: user_uuid
          }),
        },
      );

      const data = await res.json();

      if (res.status === 200) {
        setShowConfirm(false);
        onSuccess && onSuccess();
        onClose();
        toast.success(data.message);
      } else {
        toast.error(
          data.message || "An error occurred while deleting the meeting.",
        );
        throw new Error(data.message || "Failed to delete meeting");
      }
    } catch (err) {
      console.error("MeetingFormModal: Error deleting meeting", err);
      toast.error(
        err.message || "An error occurred while deleting the meeting.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const start = moment(date);
    return start.format("dddd, MMMM D");
  };

  const formatTimeRange = (start, end) => {
    if (!start || !end) return "—";
    return `${moment(start).format("h:mm")} – ${moment(end).format("h:mm a")}`;
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Meeting link copied to clipboard");
  
  };

  const calculateMeetingInterval = (start, end) => {
    if (!start || !end) return "—";
    const startMoment = moment(start);
    const endMoment = moment(end);
    const duration = moment.duration(endMoment.diff(startMoment));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    let result = "";
    if (hours > 0) result += `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    if (minutes > 0) {
      if (result) result += " ";
      result += `${minutes} ${minutes === 1 ? "Min" : "Mins"}`;
    }

    return result || "—";
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Memoized sub-component for detail rows to improve performance
  const DetailRow = React.memo(({ icon: Icon, children }) => (
    <div style={customStyles.gcalRow}>
      <div style={customStyles.iconWrap}>
        {Icon && <Icon size={22} color="#5f6368" />}
      </div>
      <div style={customStyles.rowContent}>{children}</div>
    </div>
  ));

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      className="gcal-modal"
      style={{ zIndex: 99999 }}
    >
      <Modal.Header closeButton={false}>
        <div className="evntContainer" style={customStyles.actionHeader}>
          <div className="eventTitle" style={customStyles.headerTitle}>
            {viewMode
              ? "Event Details"
              : isUpdate
                ? "Edit Event"
                : "Create Event"}
          </div>
          <div style={customStyles.actionGroup}>
            {viewMode &&
              isUpdate &&
              initialData &&
              currentUser &&
              initialData.organizer.toLowerCase() === currentUser.email.toLowerCase() && (
                <React.Fragment>
                  <button
                    onClick={() => setViewMode(false)}
                    style={customStyles.actionBtn}
                    title="Edit event"
                  >
                    <MdEdit size={20} />
                  </button>
                  <button
                    onClick={() => setShowConfirm(true)}
                    style={{ ...customStyles.actionBtn, color: "#d93025" }}
                    title="Delete event"
                  >
                    <MdDelete size={20} />
                  </button>
                </React.Fragment>
              )}
            <button
              onClick={onClose}
              style={customStyles.actionBtn}
              title="Close"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={customStyles.body}>
        {viewMode ? (
          <div style={customStyles.gcalDetails}>
            {/* Title Section */}
            <div style={customStyles.gcalRow}>
              <div
                style={{
                  ...customStyles.iconPlaceholder,
                  background: "#006EFA",
                }}
              />
              <div style={customStyles.titleWrap}>
                <h2 style={customStyles.gcalTitle}>
                  {formValue.title || "Untitled"}
                </h2>
                <p style={customStyles.gcalTimeSub}>
                  {formatDateTime(formValue.start_time)} ⋅{" "}
                  {formatTimeRange(formValue.start_time, formValue.end_time)}
                </p>
                <p style={customStyles.recurrenceText}>Does not repeat</p>
              </div>
            </div>

            {/* Meet Section */}
            {initialData && initialData.meet_link && (
              <DetailRow icon={MdVideocam}>
                <div style={customStyles.meetSection}>
                  <div style={customStyles.meetBtnContainer}>
                    <a
                      href={initialData.meet_link}
                      target="_blank"
                      rel="noreferrer"
                      style={customStyles.meetBtn}
                    >
                      Join with Google Meet
                    </a>
                    <button
                      onClick={() => handleCopyLink(initialData.meet_link)}
                      style={customStyles.copyBtn}
                      title="Copy meeting link"
                    >
                      <MdContentCopy size={18} />
                    </button>
                  </div>
                  <span style={customStyles.meetLinkText}>
                    {initialData.meet_link.replace("https://", "")}
                  </span>
                </div>
              </DetailRow>
            )}

            {/* Guests Section */}
            <DetailRow icon={MdPeopleOutline}>
              <div style={customStyles.guestsSection}>
                <div style={customStyles.guestsHeader}>
                  <span style={customStyles.guestCount}>
                    {formValue.attendees.length || 0} guests
                  </span>
                </div>
                <div style={customStyles.guestList}>
                  {initialData && initialData.organizer && (
                    <div style={customStyles.guestItem}>
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(initialData.organizer)}&size=28&background=random`}
                        alt={initialData.organizer}
                        style={customStyles.avatarImg}
                      />
                      <div style={customStyles.guestInfo}>
                        <span style={customStyles.guestName}>
                          {initialData.organizer}
                        </span>
                        <span style={customStyles.guestRole}>Organizer</span>
                      </div>
                      <MdCheckCircle
                        size={16}
                        color="#188038"
                        style={{ marginLeft: "auto" }}
                      />
                    </div>
                  )}
                  {formValue.attendees &&
                    formValue.attendees.map(
                      (email, idx) =>
                        email !== initialData.organizer && (
                          <div key={idx} style={customStyles.guestItem}>
                            <img
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(email)}&size=28&background=random`}
                              alt={email}
                              style={customStyles.avatarImg}
                            />
                            <div style={customStyles.guestInfo}>
                              <span style={customStyles.guestName}>
                                {email}
                              </span>
                            </div>
                          </div>
                        ),
                    )}
                </div>
              </div>
            </DetailRow>

            {/* Notes Section */}
            {formValue.description && (
              <DetailRow icon={MdNotes}>
                <div style={customStyles.notesSection}>
                  <div
                    style={customStyles.notesContent}
                    dangerouslySetInnerHTML={{
                      __html: formValue.description,
                    }}
                  />
                </div>
              </DetailRow>
            )}

            {/* Calendar Link */}
            {initialData && initialData.html_link && (
              <DetailRow icon={MdOpenInNew}>
                <div style={customStyles.linkSection}>
                  <a
                    href={initialData.html_link}
                    target="_blank"
                    rel="noreferrer"
                    style={customStyles.gcalLink}
                  >
                    Open in Google Calendar
                  </a>
                </div>
              </DetailRow>
            )}
          </div>
        ) : (
          <div style={customStyles.formContainer}>
            <div style={customStyles.formGroup}>
              <label style={customStyles.label}>Title</label>
              <input
                style={customStyles.input}
                value={formValue.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="Event title"
              />
            </div>

            {/* <div style={customStyles.formRow}> */}
            {/* <div style={customStyles.formRow}> */}
            <div className="meetingDateTime" style={{ ...customStyles.formGroup, flex: 1 }}>
              <label style={customStyles.label}>Start Time</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <DatePicker
                  value={formValue.start_time}
                  format="yyyy-MM-dd"
                  onChange={(date) =>
                    handleDateTimeChange("start_time", date, "date")
                  }
                  placeholder="Date"
                  style={{ width: "100%", zIndex: 99999 }}
                  cleanable={false}
                   menuClassName="DatePickerMenu"
                />
                <DatePicker
                  value={formValue.start_time}
                  format="hh:mm aa"
                  onChange={(time) =>
                    handleDateTimeChange("start_time", time, "time")
                  }
                  placeholder="Time"
                  style={{ width: "100%", zIndex: 99999 }}
                  cleanable={false}
                   menuClassName="DatePickerMenu"
                />
              </div>
            </div>
            <div style={{ ...customStyles.formGroup, flex: 1 }}>
              <label style={customStyles.label}>End Time</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <DatePicker
                  value={formValue.end_time}
                  format="yyyy-MM-dd"
                  onChange={(date) =>
                    handleDateTimeChange("end_time", date, "date")
                  }
                  placeholder="Date"
                  style={{ width: "100%", zIndex: 99999 }}
                  cleanable={false}
                  menuClassName="DatePickerMenu"
                />
                <DatePicker
                  value={formValue.end_time}
                  format="hh:mm aa"
                  onChange={(time) =>
                    handleDateTimeChange("end_time", time, "time")
                  }
                  placeholder="Time"
                  style={{ width: "100%", zIndex: 99999 }}
                  cleanable={false}
                   menuClassName="DatePickerMenu"
                />
              </div>
            </div>
            {/* </div> */}

            {(() => {
              const interval = calculateMeetingInterval(
                formValue.start_time,
                formValue.end_time,
              );
              return interval ? (
                <p style={customStyles.meetingInterval}>
                  Meeting Interval: {interval}
                </p>
              ) : null;
            })()}
            {/* </div> */}

            <div style={customStyles.formGroup}>
              <label style={customStyles.label}>Attendees</label>

              <TagInput
                value={formValue.attendees}
                placeholder="Type email and press Enter"
                trigger={["Enter", "Comma"]}
                style={customStyles.tagInput}
                onChange={(value) => {
                  const validEmails = value
                    .map((item) => item.trim())
                    .filter((item) => isValidEmail(item));

                  handleChange("attendees", validEmails);
                }}
                onBlur={(event) => {
                  const val = event.target.value.trim();

                  if (val && isValidEmail(val)) {
                    handleChange("attendees", [...formValue.attendees, val]);
                  }
                }}
              />
              <small style={customStyles.helpText}>
                Press Enter or comma after each email
              </small>
            </div>

            <div style={customStyles.formGroup}>
              <label style={customStyles.label}>Description</label>
              <textarea
                style={{ ...customStyles.input, minHeight: "80px" }}
                rows={3}
                value={formValue.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Meeting description"
              />
            </div>
          </div>
        )}
      </Modal.Body>

      {!viewMode && (
        <Modal.Footer>
          <Button appearance="subtle" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : isUpdate ? "Update" : "Schedule"}
          </Button>
        </Modal.Footer>
      )}
      <style>{`
        .gcal-modal .rs-modal-content {
        padding:0;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2);
          animation: gCalFadeIn 0.2s ease-out;
        }
        .evntContainer {
                padding: 12px !important;
        border-bottom: 1px solid #dce1e9 !important;
        }
        .gcal-modal .rs-modal-footer {
    display: flex;
    -ms-flex-pack: end;
    justify-content: end;
    border: none;
    padding: 12px !important;
    border-top: 1px solid #dce1e9 !important;
}
        .eventTitle{
        color: #333;
        font-family: Poppins;
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        }
        @keyframes gCalFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .gcal-modal .rs-modal-header {
          padding: 0;
          border-bottom: none;
        }
        .rs-tag-input-tag {
          background: #e8f0fe !important;
          color: #1a73e8 !important;
          border: 1px solid #d2e3fc !important;
        }
          .DatePickerMenu{
            z-index: 99999 !important;
          }
           .rs-modal-body {
    overflow-y: hidden !important;
}
      `}</style>

      {/* RSuite Confirmation Modal */}
      <Modal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        size="xs"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header>
          <Modal.Title>Delete Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this event? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConfirm(false)} appearance="subtle">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMeeting}
            appearance="primary"
            color="red"
            loading={deleteLoading}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

const customStyles = {
  actionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  actionGroup: {
    display: "flex",
    gap: "4px",
  },
  actionBtn: {
    background: "transparent",
    border: "none",
    padding: "8px",
    borderRadius: "50%",
    cursor: "pointer",
    color: "#5f6368",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s, color 0.2s",
    outline: "none",
  },
  body: {
    padding: "0 24px 24px",
    overflowY: "auto",
    maxHeight: "80vh",
  },
  gcalDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  gcalRow: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
  },
  rowContent: {
    flex: 1,
  },
  iconPlaceholder: {
    width: "16px",
    height: "16px",
    borderRadius: "4px",
    marginTop: "8px",
    flexShrink: 0,
    marginLeft: "4px",
  },
  iconWrap: {
    width: "24px",
    display: "flex",
    justifyContent: "center",
    marginTop: "2px",
    flexShrink: 0,
  },
  titleWrap: {
    display: "flex",
    flexDirection: "column",
  },
  gcalTitle: {
    fontSize: "22px",
    fontWeight: 400,
    color: "#1f1f1f",
    margin: "0 0 4px 0",
    lineHeight: "28px",
  },
  gcalTimeSub: {
    fontSize: "14px",
    color: "#3c4043",
    margin: 0,
  },
  meetingInterval: {
    fontSize: "13px",
    color: "#5f6368",
    margin: "4px 0 0 0",
    fontWeight: 500,
  },
  recurrenceText: {
    fontSize: "12px",
    color: "#70757a",
    margin: "2px 0 0 0",
  },
  meetSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  meetBtnContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  meetBtn: {
    background: "#1a73e8",
    color: "#fff",
    padding: "10px 24px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 500,
    textDecoration: "none",
    display: "inline-block",
    boxShadow:
      "0 1px 2px rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)",
    transition: "background 0.2s, box-shadow 0.2s",
  },
  copyBtn: {
    background: "transparent",
    border: "none",
    color: "#5f6368",
    cursor: "pointer",
    padding: "8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    transition: "background 0.2s",
  },
  meetLinkText: {
    fontSize: "12px",
    color: "#70757a",
  },
  guestsSection: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  guestCount: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#3c4043",
  },
  guestList: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  guestItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "#7986cb",
    color: "#fff",
    fontSize: "13px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarImg: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  guestInfo: {
    display: "flex",
    flexDirection: "column",
  },
  guestName: {
    fontSize: "13px",
    color: "#3c4043",
  },
  guestRole: {
    fontSize: "11px",
    color: "#70757a",
  },
  notesSection: {
    width: "100%",
    color: "#3c4043",
    fontSize: "14px",
    lineHeight: "20px",
  },
  notesContent: {
    maxHeight: "200px",
    overflowY: "auto",
  },
  linkSection: {
    display: "flex",
    alignItems: "center",
  },
  gcalLink: {
    fontSize: "14px",
    color: "#1a73e8",
    textDecoration: "none",
    fontWeight: 500,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    paddingTop: "8px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  formRow: {
    display: "flex",
    gap: "16px",
  },
  label: {
    color: "#333",
    fontFamily: "Poppins",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "18px",
    marginBottom: 0,
  },
  input: {
    padding: "10px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  helpText: {
    fontSize: "11px",
    color: "#999",
    marginTop: "2px",
  },
  tagInput: {
    width: "100%",
    fontSize: "14px",
  },
};

export default MeetingFormModal;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/MeetingFormModal.js